import { Link as GatsbyLink, graphql, useStaticQuery } from 'gatsby';
import React from 'react';

import { Box, Container, Stack, Typography, useTheme } from '@mui/material';

import {
  baseLogoHeight, baseLogoWidth
} from './header';
import { Link } from './link';
import { LinkGroupProps } from './linkGroup';
import { LinkList } from './linkList';
import LogoList from './logo-list';
import { ContentfulAssetImage } from './ui';

const useStyles = () => ({
  background: {
    background:
      "linear-gradient(172deg, rgba(249, 240, 255, 0.47) 64.14%, rgba(226, 241, 255, 0.47) 181.88%)",
    pt: { xs: 4, sm: 4, md: 10 },
    pb: { xs: 12, sm: 12, md: 10 },
    mt: 0,
  },
})

export default function Footer() {
  const styles = useStyles()
  const theme = useTheme();

  const data: Queries.FooterContentQuery = useStaticQuery(graphql`
    query FooterContent {
      contentfulLayoutFooter {
        id
        logo {
          url
          id
          title      
          __typename
          contentful_id
          file {
            contentType
          }      
          localFile {
            childImageSharp { 
              gatsbyImageData(placeholder: NONE, width: 150, height: 47)
            }
          }
        }
        navItems {
          id
          navItemType: __typename
          ... on ContentfulNavItemGroup {
            label: name
            links: navItems {
              id
              url: href
              label: text
              description
              mobileLabel
              # icon {
              #   url
              #   id
              #   title
              #   gatsbyImageData
              # }
            }
          }
        }
        logoLists {
          ... on ContentfulHomepageLogoList {
            id
            text
            title
            kicker
            kickerIcon {      
              url
              id
              title      
              __typename
              contentful_id
              file {
                contentType
              }      
              localFile {
                childImageSharp { 
                  gatsbyImageData(height: 20, width: 20)
                }
              }
            }
            logos {
              id
              alt
              image {
                url
                id
                title      
                __typename
                contentful_id
                file {
                  contentType
                }      
                localFile {
                  childImageSharp { 
                    gatsbyImageData (
                      layout: CONSTRAINED
                      height: 40
                      quality: 100
                    )
                  }
                }
            }
          }
          }
        }
        copyright
        # socialLinks {
        #   id
        #   service
        #   username
        # }
      }
    }
  `)

  const { navItems, copyright, logoLists } = data.contentfulLayoutFooter

  return (
    <Box component="footer" sx={styles.background}>
      <Container>
        <Stack direction="column" spacing={4}>
          <Stack
            gap={{ xs: 5, sm: 5, md: 8, lg: 14, xl: 20 }}
            direction={{ xs: "column", md: "row" }}
          >
            <Stack
              direction={"column"}
              spacing={2}
              sx={{
                maxWidth: {
                  xs: "inherit",
                  sm: "inherit",
                  md: "180px",
                  lg: "180px",
                  xl: "180px",
                },
              }}
            >
              <Box sx={{ margin: '0 auto!important' }}>
                <Link forceLink url={"/"} relativeLinkComponent={GatsbyLink}>
                  <Box sx={{ display: "none" }}>Home</Box>
                  <ContentfulAssetImage
                    asset={data.contentfulLayoutFooter.logo}
                    objectFit="cover"
                    imgStyle={{
                      width: baseLogoWidth + "px",
                      height: baseLogoHeight + "px",
                    }}
                  />
                </Link>
              </Box>
              <Typography
                variant="body2"
                sx={{
                  textAlign: {
                    xs: "center",
                    sm: "center",
                    md: "inherit",
                    lg: "inherit",
                    xl: "inherit",
                  },
                }}
              >
                Seeka acknowledges the first nations people of the land we
                live, work and operate.
              </Typography>
            </Stack>

            <Stack
              direction={"row"}
              spacing={{ xs: 3, sm: 6, md: 10 }}
              useFlexGap
              flexWrap="wrap"
              justifyContent={{
                xs: "space-between",
                sm: "space-between",
                md: "flex-start",
                lg: "flex-start",
              }}
            >
              {navItems && navItems.length > 0 ? (
                <LinkList
                  headerComponent="h6"
                  forceLink
                  LinkProps={{ sx: { color: theme.palette.text.primary }, fontSize: { xs: '0.8rem', sm: '0.9rem' } }}
                  links={navItems as any as LinkGroupProps[]}
                  StackProps={{ width: '100%', direction: { xs: "row", sm: "row", md: "row" }, alignItems: { xs: 'flex-start', sm: 'flex-start', md: 'flex-start' }, justifyContent: { xs: 'space-between', sm: 'space-between', md: 'center' }, gap: { xs: 2, sm: 2, md: 8 } }}
                  relativeLinkComponent={GatsbyLink}
                  LinkGroupItemsTypographyProps={{ variant: 'body2', color: theme.palette.primary.main }}
                  LinkGroupItemsStackProps={{
                    pt: { xs: 1, sm: 1, md: 0 },
                    gap: { xs: 1, sm: 1, md: 2 }
                  }}
                />
              ) : <></>}
            </Stack>
          </Stack>

          {logoLists && logoLists.length > 0 ?
            logoLists.map((logoList) => (
              <LogoList
                headerComponent='h5'
                key={logoList.id}
                imgStyle={{ height: "40px", width: "auto" }}
                RootStackProps={{ spacing: 1 }}
                {...logoList}
              />)) : <></>
          }

          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="body2"
              color="inherit"
              sx={{ marginTop: "20px" }}
            >
              {copyright}
            </Typography>
          </Box>
        </Stack>
      </Container>
    </Box>
  )
}
